"use client";

import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import {
    useCallback,
    useContext,
    useMemo,
    useRef,
    FC,
    PropsWithChildren,
    forwardRef,
    RefAttributes,
    ForwardRefExoticComponent,
    useEffect,
    useState,
} from "react";
import {
    ChatServiceContext,
    formatSearchParamsToString,
    splitArrIntoChunks,
    useLayoutProfileConfig,
    useSearchParamsQuery,
} from "@finbackoffice/site-core";
import {
    CrmContext,
    IModalForwardRefPropsV2,
    ModalsContextV2,
    UserAccountContextV2,
} from "contexts";
import { ILayoutsProfileSection, ProfileLayoutSectionEnum } from "@finbackoffice/fe-core";
import classNames from "classnames";
import { usePathname, useRouter } from "next/navigation";
import PersonalDetailsMobile from "components/account/details/personal/PersonalDetailsMobile";
import AccountTransactions from "components/account/transactions/AccountTransactions";
import Modal from "components/base/modal/Modal";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import { ModalTypes, RouterQuery } from "utils/constants";
import { ModalStyleTypeEnum } from "components/base/modal/constants/modal-config";
import Button from "components/base/button/Button";
import ResponsibleGaming from "components/account/details/responsible-gaming/ResponsibleGaming";
import TwoFactor from "components/account/details/2fa/TwoFactor";
import AccountReferralModalContent from "components/account/referral/AccountReferralModalContent";
import styles from "./sections.module.sass";

type IMainSectionModalProps = {
    handleClose?: () => void;
    onClose?: () => void;
    styleClass?: string;
    closeButton?: boolean;
};

const MainSectionModal: ForwardRefExoticComponent<
    IMainSectionModalProps & PropsWithChildren & RefAttributes<IModalForwardRefPropsV2>
> = forwardRef(({ children, onClose, handleClose, styleClass, closeButton }, ref) => {
    return (
        <Modal
            priority
            ref={ref}
            type={ModalTypes.PROFILE_MAIN_SECTION_MODAL}
            styleClass={classNames(styles.profileDetailsModal, styleClass)}
            styleConfigType={ModalStyleTypeEnum.ProfileDetails}
            animateVariant="bottom"
            closeButton={closeButton}
            onClose={onClose}>
            <>
                {children}
                <Button
                    name="close"
                    type="button"
                    className={styles.closeButton}
                    onClick={handleClose}
                    data-testid="modal-close-button">
                    <Translate tid="base_close" />
                </Button>
            </>
        </Modal>
    );
});

const MainSection: FC = () => {
    const router = useRouter();
    const pathname = usePathname();
    const { query } = useSearchParamsQuery();

    const { config: mainSectionLayoutConfig, getFilteredLayoutProfileConfigs } =
        useLayoutProfileConfig(ProfileLayoutSectionEnum.Main);
    const mainSectionLayoutConfigFiltered = getFilteredLayoutProfileConfigs(
        mainSectionLayoutConfig?.config,
    );
    const { subdirectory: subDirectoryParam } = query;

    const { isUserVerified } = useContext(UserAccountContextV2);
    const { showWidget, visible } = useContext(ChatServiceContext);
    const { openWalletModal } = useContext(ModalsContextV2);
    const { openGamificationUi } = useContext(CrmContext);

    const [subdirectory, setSubdirectory] = useState<RouterQuery>(
        (subDirectoryParam as RouterQuery) || "",
    );

    const mainModalRef = useRef<IModalForwardRefPropsV2>(null);

    const sectionConfigChunks: ILayoutsProfileSection[][] = useMemo(
        () => splitArrIntoChunks(mainSectionLayoutConfigFiltered ?? [], 2),
        [mainSectionLayoutConfigFiltered],
    );

    useEffect(() => {
        if (
            subDirectoryParam &&
            subdirectory !== subDirectoryParam &&
            mainModalRef.current &&
            !mainModalRef.current.isOpened
        ) {
            setSubdirectory(subDirectoryParam as RouterQuery);
            mainModalRef.current.open();
        }
    }, [subDirectoryParam, subdirectory]);

    const onMainSectionModalClose = useCallback(() => {
        if (subDirectoryParam) {
            const q = { ...query };
            delete q.subdirectory;
            delete q.scope;
            router.push(`${pathname}?${formatSearchParamsToString(q)}`);
        }
    }, [pathname, query, router, subDirectoryParam]);

    const onChatServiceClick = useCallback(() => {
        showWidget();
    }, [showWidget]);

    const updateSubdirectory = useCallback((val: RouterQuery) => {
        setSubdirectory(val);
        mainModalRef.current?.open();
    }, []);

    const renderLink = useCallback(
        (item: ILayoutsProfileSection) => {
            let content;
            switch (item.name) {
                case ProfileLayoutSectionEnum.Details:
                    content = (
                        <Button
                            key={item.name}
                            type="button"
                            variant="custom"
                            data-testid="account-menu-item-details"
                            onClick={() => updateSubdirectory(RouterQuery.Personal)}>
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-my-details.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            {!isUserVerified && (
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/question-icon.svg`}
                                    className="svg-icon-unverified"
                                />
                            )}
                            <p>
                                <Translate
                                    tid="account_menu_details_heading"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Transactions:
                    content = (
                        <Button
                            key={item.name}
                            type="button"
                            variant="custom"
                            onClick={() => updateSubdirectory(RouterQuery.Transactions)}
                            data-testid="account-menu-item-transactions">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-balance.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                <Translate
                                    tid="account_menu_transactions_heading"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.ResponsibleGaming:
                    content = (
                        <Button
                            type="button"
                            key={item.name}
                            variant="custom"
                            onClick={() => updateSubdirectory(RouterQuery.ResponsibleGaming)}
                            data-testid="account-menu-item-responsible-gaming">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-responsible-gaming.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                <Translate
                                    tid="account_menu_details_responsibleGaming"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Referral:
                    content = (
                        <Button
                            type="button"
                            key={item.name}
                            variant="custom"
                            onClick={() => updateSubdirectory(RouterQuery.Referral)}
                            data-testid="account-menu-item-referral">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-promotions-referral.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                <Translate
                                    tid="account_menu_referral_heading"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum["2FA"]:
                    content = (
                        <Button
                            type="button"
                            key={item.name}
                            variant="custom"
                            onClick={() => updateSubdirectory(RouterQuery["2FA"])}
                            data-testid="account-menu-item-2step">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-2step.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                <Translate
                                    tid="account_menu_details_2fa"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.ChatService:
                    content = (
                        <Button
                            key={item.name}
                            type="button"
                            onClick={onChatServiceClick}
                            disabled={visible}
                            className={styles.menuItem}>
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-messages-livechat.svg`}
                                className="svg-icon"
                                testId="account-compact-menu-chat"
                                prefixAppName
                            />
                            <p>
                                <Translate
                                    tid="account_menu_chat_service_heading"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Wallet:
                    content = (
                        <Button
                            key={item.name}
                            type="button"
                            onClick={() => {
                                openWalletModal();
                            }}
                            className={styles.menuItem}
                            data-testid="account-compact-menu-wallet">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-messages-livechat.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                <Translate tid="header_wallet" />
                            </p>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Gamification:
                    content = (
                        <Button
                            key={item.name}
                            type="button"
                            onClick={() => {
                                openGamificationUi();
                            }}
                            className={styles.menuItem}
                            data-testid="account-compact-menu-gamification">
                            <Svg
                                src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-gamification.svg`}
                                className="svg-icon"
                                prefixAppName
                            />
                            <p>
                                <Translate
                                    tid="account_menu_gamification_heading"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </p>
                        </Button>
                    );
                    break;
                default:
                    return null;
            }

            return item.enable ? content : null;
        },
        [
            isUserVerified,
            updateSubdirectory,
            onChatServiceClick,
            visible,
            openWalletModal,
            openGamificationUi,
        ],
    );

    const renderContent = useMemo(() => {
        switch (subdirectory) {
            case RouterQuery.Personal:
            case RouterQuery.Verification: {
                const defaultView =
                    subdirectory === RouterQuery.Verification ? "verification" : "main";
                return <PersonalDetailsMobile defaultView={defaultView} />;
            }
            case RouterQuery.ResponsibleGaming:
                return <ResponsibleGaming />;
            case RouterQuery.Transactions:
                return <AccountTransactions />;
            case RouterQuery["2FA"]:
                return <TwoFactor />;

            case RouterQuery.Referral:
                return <AccountReferralModalContent />;
            default:
                return null;
        }
    }, [subdirectory]);

    return (
        <>
            <nav className={styles.subMenu} data-testid="account-main-tab-container">
                {sectionConfigChunks.map((chunk, index) =>
                    chunk.length ? <li key={index}>{chunk.map(renderLink)}</li> : null,
                )}
            </nav>
            <MainSectionModal
                onClose={onMainSectionModalClose}
                ref={mainModalRef}
                closeButton={
                    ![
                        RouterQuery.Personal,
                        RouterQuery["2FA"],
                        RouterQuery.ResponsibleGaming,
                    ].includes(subdirectory)
                }
                handleClose={() => {
                    onMainSectionModalClose();
                    void mainModalRef.current?.close();
                }}>
                {renderContent}
            </MainSectionModal>
        </>
    );
};

export default MainSection;
